import React, { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { generateInviteLink } from '../../data-layer/business/service';
import { showNotificationAsync } from '../../data-layer/notifications/actions';
import { useDispatch } from '../../data-layer/useDispatch';
import { Dialog, DialogContent } from '../../ui/feedback/Dialog';
import { IconRetry } from '../../ui/icon/IconRetry';
import { Button } from '../../ui/input/Button';
import { FormControl } from '../../ui/input/FormControl';
import { Input } from '../../ui/input/Input';
import { Flex } from '../../ui/layout/Flex';
import { Typography } from '../../ui/typography/Typography';
import { ymTrackSettingsCopyInvite } from '../../utils/metrika';
import { share } from '../../utils/share';
import { useBackButton } from '../../utils/useBackButton';
import { useTranslation } from '../../utils/useTranslation';
import { useCurrentUser } from '../users/useCurrentUser';

export function InviteMemberDialog() {
    const params = useParams();
    const businessId = params.businessId!;
    const dispatch = useDispatch();
    const t = useTranslation();
    const onNavigateBack = useBackButton();
    const currentUser = useCurrentUser();

    const [{ error, loading: inviteLoading, value: inviteLink }, onRefresh] =
        useAsyncFn(async () => {
            try {
                return await generateInviteLink(businessId);
            } catch (err) {
                throw new Error(t('Не удалось сгенерировать ссылку'));
            }
        }, [businessId, dispatch]);

    const onCopyLink = useCallback(async () => {
        ymTrackSettingsCopyInvite({
            page: 'Настройки бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
        await share({
            text: inviteLink,
        });
        dispatch(showNotificationAsync(t('Ссылка успешно скопирована')));
    }, [inviteLink, currentUser?.profile?.publicId, currentUser?.login, dispatch]);

    useEffect(() => {
        onRefresh();
    }, [onRefresh]);

    return (
        <Dialog
            onClose={onNavigateBack}
            height="auto"
            borderRadius={3}
            maxWidth={{ xs: 'calc(100% - 32px)', sm: 580 }}
        >
            <DialogContent>
                <Typography pb={2} variant="title3" weight="bold">
                    {t('Создать инвайт')}
                </Typography>
                <Typography pb={1}>
                    {t(
                        'Отправьте ссылку на регистрацию в качестве администратора. Ссылка перестанет действовать после первой активации',
                    )}
                </Typography>

                <Flex width="100%" direction="row" spacing={2} alignItems="center">
                    <FormControl
                        label="Ссылка на приглашение"
                        disabled
                        error={Boolean(error)}
                        message={error?.message}
                    >
                        <Input value={inviteLink} type="text" autoComplete="off" />
                    </FormControl>
                    <Button
                        disabled={inviteLoading}
                        variant="filled"
                        color="secondary"
                        onClick={onRefresh}
                    >
                        <IconRetry size={20} />
                    </Button>
                </Flex>

                <Flex width="100%" pt={2} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button
                        disabled={!inviteLink}
                        width={{ xs: '100%', sm: '50%' }}
                        color="primary"
                        onClick={onCopyLink}
                    >
                        {t('Скопировать ссылку')}
                    </Button>
                    <Button
                        width={{ xs: '100%', sm: '50%' }}
                        variant="outlined"
                        onClick={onNavigateBack}
                    >
                        {t('Закрыть')}
                    </Button>
                </Flex>
            </DialogContent>
        </Dialog>
    );
}
